/**
 * Secure storage wrapper for sensitive data (auth tokens, employee portal session).
 *
 * Uses sessionStorage (cleared when tab closes) instead of localStorage so tokens
 * don't persist after the user closes the browser. Values are base64-encoded to
 * prevent casual inspection in dev tools.
 *
 * For full XSS hardening, migrate auth tokens to httpOnly cookies on the backend.
 */

const PREFIX = '__stp_';
const store = typeof window !== 'undefined' ? window.sessionStorage : null;

const encode = (value) => {
  try {
    return btoa(encodeURIComponent(JSON.stringify(value)));
  } catch (e) {
    console.error('secureStorage encode error:', e);
    return null;
  }
};

const decode = (encoded) => {
  try {
    return JSON.parse(decodeURIComponent(atob(encoded)));
  } catch (e) {
    console.error('secureStorage decode error:', e);
    return null;
  }
};

export const secureStorage = {
  setItem(key, value) {
    if (!store) return;
    try {
      store.setItem(PREFIX + key, encode(value));
    } catch (e) {
      console.error('secureStorage setItem error:', e);
    }
  },

  getItem(key) {
    if (!store) return null;
    try {
      const raw = store.getItem(PREFIX + key);
      if (!raw) return null;
      return decode(raw);
    } catch (e) {
      console.error('secureStorage getItem error:', e);
      return null;
    }
  },

  removeItem(key) {
    if (!store) return;
    store.removeItem(PREFIX + key);
  },

  clear() {
    if (!store) return;
    Object.keys(store)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => store.removeItem(k));
  },
};

// One-shot migration: move any legacy localStorage entries to sessionStorage,
// then purge them from localStorage so tokens don't linger across sessions.
if (typeof window !== 'undefined' && window.localStorage && store) {
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => {
        const v = window.localStorage.getItem(k);
        if (v && !store.getItem(k)) {
          store.setItem(k, v);
        }
        window.localStorage.removeItem(k);
      });
  } catch (e) {
    console.error('secureStorage legacy localStorage migration failed:', e);
  }
}
